import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';
import { useApp } from '../context/AppContext';
import { Badge } from '../components/Badge';
import { StatCard } from '../components/StatCard';
import { formatCents } from '../utils/format';
import { enumLabel, EXPENSE_CATEGORY_VALUES } from '../utils/i18nLabels';

const emptyExp = {
  employee_id: '',
  employee_name: '',
  amount_cents: 10000,
  currency: 'ARS',
  category: 'travel',
  description: '',
  expense_date: new Date().toISOString().slice(0, 10),
};

function syncLabel(st, t) {
  if (st === 'not_synced') return t('expenses.notSynced');
  if (st === 'pending') return t('expenses.pending');
  if (st === 'pending_sync') return t('expenses.pending');
  if (st === 'synced') return t('expenses.synced');
  if (st === 'error') return t('expenses.error');
  return st;
}

function expenseStatusLabel(t, status) {
  const k = `expenses.status_${status}`;
  const v = t(k);
  return v === k ? t(`status.${status}`) : v;
}

export function ExpenseReports() {
  const { t } = useApp();
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyExp);
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(false);

  async function load() {
    setLoading(true);
    setLoadErr(false);
    try {
      const [ex, em] = await Promise.all([
        api.get('/api/expense-reports'),
        api.get('/api/employees'),
      ]);
      setRows(Array.isArray(ex.data) ? ex.data : []);
      setEmployees(Array.isArray(em.data) ? em.data : []);
    } catch {
      setRows([]);
      setLoadErr(true);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function call(id, fn) {
    setMsg(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setMsg(e?.response?.data?.detail || t('common.error'));
    }
  }

  async function create() {
    await api.post('/api/expense-reports', {
      ...form,
      amount_cents: Math.round(Number(form.amount_cents)),
    });
    setModal(false);
    setForm(emptyExp);
    await load();
  }

  async function syncFinance(id) {
    const { data } = await api.post(`/api/integrations/finance/expense-reports/${id}/sync`);
    setMsg(data?.message || 'OK');
    await load();
  }

  function badgeVariant(status) {
    if (status === 'paid' || status === 'finance_approved') return 'finance_approved';
    if (status === 'rejected' || status === 'cancelled') return 'rejected';
    if (status === 'draft') return 'draft';
    if (status === 'submitted') return 'submitted';
    if (status === 'manager_approved') return 'manager_approved';
    return 'pending';
  }

  const metrics = useMemo(() => {
    const activeRows = rows.filter((r) => r.status !== 'cancelled');
    const totalCents = activeRows
      .filter((r) => r.status !== 'rejected')
      .reduce((acc, r) => acc + (Number(r.amount_cents) || 0), 0);
    const pendingApproval = rows.filter((r) => ['draft', 'submitted', 'manager_approved'].includes(r.status)).length;
    const approvedFinance = rows.filter((r) => r.status === 'finance_approved').length;
    const paid = rows.filter((r) => r.status === 'paid').length;
    const rejected = rows.filter((r) => r.status === 'rejected').length;
    const pendingManager = rows.filter((r) => r.status === 'submitted').length;
    const pendingFinance = rows.filter((r) => r.status === 'manager_approved').length;
    const pendingSync = rows.filter((r) => r.finance_sync_status === 'not_synced' || r.finance_sync_status === 'pending_sync').length;
    return { totalCents, pendingApproval, approvedFinance, paid, rejected, pendingManager, pendingFinance, pendingSync };
  }, [rows]);

  if (loading && rows.length === 0) {
    return <div className="empty-state empty-state--panel">{t('expenses.loading')}</div>;
  }

  return (
    <div className="page-stack expenses-page expenses-page--v20">
      <header className="expenses-suite-hero panel panel--elevated">
        <p className="expenses-suite-hero__eyebrow">{t('expenses.suiteHeroEyebrow')}</p>
        <h1 className="expenses-suite-hero__title">{t('expenses.suiteHeroTitle')}</h1>
        <p className="expenses-suite-hero__lead">{t('expenses.heroLead')}</p>
      </header>

      <div className="finance-banner finance-banner--stacked">
        <span>{t('expenses.financeBanner')}</span>
        <p className="finance-banner__sub">{t('expenses.financeFlowLong')}</p>
      </div>

      <div className="expense-timeline" aria-label={t('expenses.timelineTitle')}>
        <span className="expense-timeline__title">{t('expenses.timelineTitle')}</span>
        <ol className="expense-timeline__track">
          <li className="expense-timeline__step is-done">
            <span className="expense-timeline__dot" />
            {t('expenses.timelineDraft')}
          </li>
          <li className="expense-timeline__step is-done">
            <span className="expense-timeline__dot" />
            {t('expenses.timelineSent')}
          </li>
          <li className="expense-timeline__step">
            <span className="expense-timeline__dot" />
            {t('expenses.timelineManager')}
          </li>
          <li className="expense-timeline__step">
            <span className="expense-timeline__dot" />
            {t('expenses.timelineFinance')}
          </li>
          <li className="expense-timeline__step">
            <span className="expense-timeline__dot" />
            {t('expenses.timelinePaid')}
          </li>
        </ol>
      </div>

      <div className="people-suite-kpis expenses-v17-kpis">
        <StatCard icon="∑" label={t('expenses.kpiTotal')} value={formatCents(metrics.totalCents, 'ARS')} tone="accent" />
        <StatCard icon="◷" label={t('expenses.kpiPendingApproval')} value={metrics.pendingApproval} tone="warning" />
        <StatCard icon="✓" label={t('expenses.kpiApprovedFinance')} value={metrics.approvedFinance} tone="success" />
        <StatCard icon="★" label={t('expenses.kpiPaid')} value={metrics.paid} />
        <StatCard icon="✕" label={t('expenses.kpiRejected')} value={metrics.rejected} />
        <StatCard icon="⧗" label={t('expenses.kpiPendingSync')} value={metrics.pendingSync} tone="warning" />
      </div>

      <div className="metrics-row metrics-row--secondary expenses-v17-subkpi">
        <div className="metric-chip metric-chip--accent">
          <span className="metric-chip__value">{metrics.pendingManager}</span>
          <span className="metric-chip__label">{t('expenses.metricsManager')}</span>
        </div>
        <div className="metric-chip metric-chip--accent">
          <span className="metric-chip__value">{metrics.pendingFinance}</span>
          <span className="metric-chip__label">{t('expenses.metricsFinance')}</span>
        </div>
      </div>

      <section className="panel panel--elevated expenses-v17-finance-next">
        <h3 className="panel__title">{t('expenses.financeReadyTitle')}</h3>
        <ul className="pi-card-list">
          <li>{t('expenses.financeReady1')}</li>
          <li>{t('expenses.financeReady2')}</li>
          <li>{t('expenses.financeReady3')}</li>
          <li>{t('expenses.financeReady4')}</li>
        </ul>
        <h4 className="panel__title" style={{ marginTop: '1rem', fontSize: '1rem' }}>
          {t('expenses.financeNextTitle')}
        </h4>
        <p className="panel__muted">{t('expenses.financeNextLead')}</p>
      </section>

      {loadErr && <div className="banner banner--info">{t('common.error')}</div>}
      {msg && <div className="banner banner--info">{msg}</div>}

      <div className="toolbar toolbar--card">
        <div className="toolbar__grow" />
        <button type="button" className="btn btn--primary" onClick={() => setModal(true)}>
          {t('expenses.new')}
        </button>
      </div>
      <section className="panel panel--elevated expenses-next-actions">
        <h3 className="panel__title">{t('expenses.nextActionsTitle')}</h3>
        <ol className="pi-card-list">
          <li>{t('expenses.nextAction1')}</li>
          <li>{t('expenses.nextAction2')}</li>
          <li>{t('expenses.nextAction3')}</li>
        </ol>
      </section>

      <div className="table-wrap table-wrap--rounded hide-sm">
        <table className="data-table data-table--comfortable">
          <thead>
            <tr>
              <th>{t('expenses.fieldEmployee')}</th>
              <th>{t('expenses.category')}</th>
              <th>{t('expenses.amount')}</th>
              <th>{t('expenses.status')}</th>
              <th>{t('expenses.financeSync')}</th>
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="empty-inline">
                  {t('expenses.empty')}
                </td>
              </tr>
            ) : (
              rows.map((ex) => (
                <tr key={ex.id}>
                  <td>{ex.employee_name}</td>
                  <td>{enumLabel(t, 'expenseCategories', ex.category)}</td>
                  <td>{formatCents(ex.amount_cents, ex.currency)}</td>
                  <td>
                    <Badge variant={badgeVariant(ex.status)}>{expenseStatusLabel(t, ex.status)}</Badge>
                  </td>
                  <td>
                    <span className="text-sm text-muted">
                      {syncLabel(ex.finance_sync_status, t)}
                      {ex.finance_sync_status === 'not_synced' && ` · ${t('expenses.prepared')}`}
                    </span>
                  </td>
                  <td>
                    <div className="action-stack">
                      {ex.status === 'draft' && (
                        <button
                          type="button"
                          className="btn btn--sm btn--secondary"
                          onClick={() => call(ex.id, () => api.post(`/api/expense-reports/${ex.id}/submit`))}
                        >
                          {t('expenses.submit')}
                        </button>
                      )}
                      {ex.status === 'submitted' && (
                        <>
                          <button
                            type="button"
                            className="btn btn--sm btn--primary"
                            onClick={() =>
                              call(ex.id, () => api.post(`/api/expense-reports/${ex.id}/manager-approve`))
                            }
                          >
                            {t('expenses.managerApprove')}
                          </button>
                          <button
                            type="button"
                            className="btn btn--sm btn--danger"
                            onClick={() => call(ex.id, () => api.post(`/api/expense-reports/${ex.id}/reject`))}
                          >
                            {t('expenses.reject')}
                          </button>
                        </>
                      )}
                      {ex.status === 'manager_approved' && (
                        <>
                          <button
                            type="button"
                            className="btn btn--sm btn--primary"
                            onClick={() =>
                              call(ex.id, () => api.post(`/api/expense-reports/${ex.id}/finance-approve`))
                            }
                          >
                            {t('expenses.financeApprove')}
                          </button>
                          <button
                            type="button"
                            className="btn btn--sm btn--danger"
                            onClick={() => call(ex.id, () => api.post(`/api/expense-reports/${ex.id}/reject`))}
                          >
                            {t('expenses.reject')}
                          </button>
                        </>
                      )}
                      {ex.status === 'finance_approved' && (
                        <button
                          type="button"
                          className="btn btn--sm btn--primary"
                          onClick={() => call(ex.id, () => api.post(`/api/expense-reports/${ex.id}/mark-paid`))}
                        >
                          {t('expenses.markPaid')}
                        </button>
                      )}
                      <button type="button" className="btn btn--sm btn--ghost" onClick={() => syncFinance(ex.id)}>
                        {t('expenses.syncFinance')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <section className="panel panel--elevated expense-mobile-stack hide-md-up" aria-label={t('expenses.mobileCardsTitle')}>
        <h3 className="panel__title">{t('expenses.mobileCardsTitle')}</h3>
        <p className="panel__muted text-sm">{t('expenses.mobileCardsHint')}</p>
        {rows.length === 0 ? (
          <p className="panel__muted text-sm">{t('expenses.empty')}</p>
        ) : (
          <ul className="expense-mcard-list">
            {rows.map((ex) => (
              <li key={ex.id} className="expense-mcard">
                <div className="expense-mcard__top">
                  <strong>{ex.employee_name}</strong>
                  <Badge variant={badgeVariant(ex.status)}>{expenseStatusLabel(t, ex.status)}</Badge>
                </div>
                <div className="expense-mcard__row">
                  <span className="text-muted text-sm">{enumLabel(t, 'expenseCategories', ex.category)}</span>
                  <span className="expense-mcard__amt">{formatCents(ex.amount_cents, ex.currency)}</span>
                </div>
                <p className="text-muted text-sm expense-mcard__sync">{syncLabel(ex.finance_sync_status, t)}</p>
                <div className="expense-mcard__actions action-stack">
                  {ex.status === 'draft' && (
                    <button
                      type="button"
                      className="btn btn--sm btn--secondary"
                      onClick={() => call(ex.id, () => api.post(`/api/expense-reports/${ex.id}/submit`))}
                    >
                      {t('expenses.submit')}
                    </button>
                  )}
                  {ex.status === 'submitted' && (
                    <>
                      <button
                        type="button"
                        className="btn btn--sm btn--primary"
                        onClick={() => call(ex.id, () => api.post(`/api/expense-reports/${ex.id}/manager-approve`))}
                      >
                        {t('expenses.managerApprove')}
                      </button>
                      <button
                        type="button"
                        className="btn btn--sm btn--danger"
                        onClick={() => call(ex.id, () => api.post(`/api/expense-reports/${ex.id}/reject`))}
                      >
                        {t('expenses.reject')}
                      </button>
                    </>
                  )}
                  {ex.status === 'manager_approved' && (
                    <>
                      <button
                        type="button"
                        className="btn btn--sm btn--primary"
                        onClick={() => call(ex.id, () => api.post(`/api/expense-reports/${ex.id}/finance-approve`))}
                      >
                        {t('expenses.financeApprove')}
                      </button>
                      <button
                        type="button"
                        className="btn btn--sm btn--danger"
                        onClick={() => call(ex.id, () => api.post(`/api/expense-reports/${ex.id}/reject`))}
                      >
                        {t('expenses.reject')}
                      </button>
                    </>
                  )}
                  {ex.status === 'finance_approved' && (
                    <button
                      type="button"
                      className="btn btn--sm btn--primary"
                      onClick={() => call(ex.id, () => api.post(`/api/expense-reports/${ex.id}/mark-paid`))}
                    >
                      {t('expenses.markPaid')}
                    </button>
                  )}
                  <button type="button" className="btn btn--sm btn--ghost" onClick={() => syncFinance(ex.id)}>
                    {t('expenses.syncFinance')}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {modal && (
        <div className="modal-backdrop" role="dialog" aria-modal>
          <div className="modal">
            <h2 className="modal__title">{t('expenses.new')}</h2>
            <div className="form-grid">
              <label className="field field--full">
                <span>{t('expenses.fieldEmployee')}</span>
                <select
                  className="input"
                  value={form.employee_id}
                  onChange={(e) => {
                    const emp = employees.find((x) => x.id === e.target.value);
                    setForm({
                      ...form,
                      employee_id: e.target.value,
                      employee_name: emp ? `${emp.first_name} ${emp.last_name}` : '',
                    });
                  }}
                >
                  <option value="">—</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>{t('expenses.amount')}</span>
                <input
                  className="input"
                  type="number"
                  value={form.amount_cents}
                  onChange={(e) => setForm({ ...form, amount_cents: e.target.value })}
                />
                <div className="text-sm text-muted expenses-v17-hint">{t('expenses.amountCentsHint')}</div>
              </label>
              <label className="field">
                <span>{t('expenses.fieldCurrency')}</span>
                <select
                  className="input"
                  value={form.currency}
                  onChange={(e) => setForm({ ...form, currency: e.target.value })}
                >
                  <option value="ARS">ARS</option>
                  <option value="USD">USD</option>
                </select>
              </label>
              <label className="field field--full">
                <span>{t('expenses.category')}</span>
                <select className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  {EXPENSE_CATEGORY_VALUES.map((c) => (
                    <option key={c} value={c}>
                      {enumLabel(t, 'expenseCategories', c)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field field--full">
                <span>{t('expenses.fieldDescription')}</span>
                <input
                  className="input"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </label>
              <label className="field">
                <span>{t('expenses.fieldExpenseDate')}</span>
                <input
                  className="input"
                  type="date"
                  value={form.expense_date}
                  onChange={(e) => setForm({ ...form, expense_date: e.target.value })}
                />
              </label>
            </div>
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setModal(false)}>
                {t('employees.cancel')}
              </button>
              <button type="button" className="btn btn--primary" onClick={create} disabled={!form.employee_id}>
                {t('employees.save')}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
